/**
 * Translation Hook
 * Provides translation functionality with language switching support
 */

import { useCallback } from 'react';
import { useUserContext } from '../contexts';
import ruTranslations from '../locales/ru.json';
import enTranslations from '../locales/en.json';
import trTranslations from '../locales/tr.json';

type TranslationKey = string;
type Translations = typeof ruTranslations;

const translations: Record<string, Translations> = {
  ru: ruTranslations,
  en: enTranslations,
  tr: trTranslations,
};

/**
 * Get nested value from object using dot notation
 * e.g., "user.balance" -> translations.user.balance
 */
function getNestedValue(obj: any, path: string): string {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return path; // Return key if not found
    }
  }
  
  return typeof current === 'string' ? current : path;
}

export function useTranslation() {
  const { user } = useUserContext();
  
  // Default to English if user language is not set
  const currentLanguage = user?.language || 'en';
  
  /**
   * Translate a key to the current language
   * @param key - Translation key in dot notation (e.g., "user.balance")
   * @param params - Optional interpolation params or fallback string
   */
  const t = useCallback((key: TranslationKey, params?: string | Record<string, string | number>): string => {
    const languageTranslations = translations[currentLanguage] || translations.en;
    let translated = getNestedValue(languageTranslations, key);
    
    // If translation not found and fallback string provided, use fallback
    if (translated === key && typeof params === 'string') {
      return params;
    }
    
    // Interpolate {{variable}} placeholders
    if (typeof params === 'object' && params !== null) {
      for (const [k, v] of Object.entries(params)) {
        translated = translated.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
      }
    }
    
    return translated;
  }, [currentLanguage]);
  
  /**
   * Get the current language code
   */
  const language = currentLanguage;
  
  /**
   * Check if a specific language is currently active
   */
  const isLanguage = useCallback((lang: string): boolean => {
    return currentLanguage === lang;
  }, [currentLanguage]);
  
  return {
    t,
    language,
    isLanguage,
  };
}
